import {Injectable} from '@angular/core';
import {Layer} from 'konva/lib/Layer';
import {Rect} from 'konva/lib/shapes/Rect';
import {IAdComponentData, ICanvasObject, ILayerData, TGraphType} from '../models/graph-kn.model-interfaces';
import {Stage} from 'konva/lib/Stage';
import {KonvaEventObject} from 'konva/lib/Node';
import {Vector2d} from 'konva/lib/types';
import {SCALE_BY} from '../models/graph-kn.cm.model-const';
import {GraphKnDataService} from './graph-kn.data.service';
import {Arrow} from 'konva/lib/shapes/Arrow';
import {Group} from 'konva/lib/Group';
import {GraphKnConnectorsLayersService} from './graph-kn.connectros-layers.service'
import {BUSINESS_PROCESS_LAYER_ID, CONTRACT_LAYER_ID, DagreNodesOnlySettings} from '../models/graph-kn.cm-api-models';
import { RectModelsMap } from '../styles/graph-node-rect-model';

@Injectable({
    providedIn: 'root'
})
export class GraphKnLayerService {
    constructor(
        private knDataService: GraphKnDataService,
        private knConnectorsService: GraphKnConnectorsLayersService,
    ) {
    }

    createLayer(stage: Stage, layoutDagreSettings: DagreNodesOnlySettings, layerId: TGraphType, canvasObjectsMap: Map<string, ICanvasObject>, bottomLayer: boolean = true, fill?: string): ILayerData {
      let rectModel = RectModelsMap.get(layerId==='bpLayer'? 'BPStatus' : 'contractStep')  
      let height = layoutDagreSettings.heigt
        if (layerId === BUSINESS_PROCESS_LAYER_ID) {
            height = this.knDataService.layoutDagreSettings.get(CONTRACT_LAYER_ID)?.heigt
        }
        let contractLayers = [...this.knDataService.layersMap].filter(el => el[0] !== 'bpLayer')
        let lastLayer = [...new Map(contractLayers).values()].at(-1)?.dragRect;
        let offsetY = (lastLayer?.getAbsolutePosition().y || 10) + (lastLayer?.height() || 0) + (rectModel!.height! / 2 * (+(this.knDataService.layersMap.size >= 1)))

        let newLayer = new Layer({
            draggable: true,
            x: layoutDagreSettings.marginX ? layoutDagreSettings.marginX : 0,
            y: offsetY
        });
        let dragLayerRect = new Rect({
            fill: fill || layoutDagreSettings.fill,
            opacity: 0,
            id: layerId,
        })
        dragLayerRect.width(layoutDagreSettings.width),
        dragLayerRect.height(layoutDagreSettings.heigt )
        dragLayerRect.cornerRadius(10)
        newLayer.add(dragLayerRect)
        canvasObjectsMap.set(layerId, {contextMenu: false})
        let selectedLayerId = (this.knDataService.selectedNodeGroup?.parent?.children.find(el => el instanceof Rect) as Rect)?.attrs.id
        this.addEventHandlersToLayer(newLayer, bottomLayer, layerId, layerId === selectedLayerId ? undefined : selectedLayerId);
        stage.add(newLayer);
        return {
            layer: newLayer,
            dimensions: {
                width: dragLayerRect.width(),
                height: dragLayerRect.height()
            },
            dragRect: dragLayerRect,
        };
    }

    addEventHandlersToLayer(layer: Layer, bottomLayer: boolean, layerId?: string, layerIdAbove?: string) {
        layer.on('wheel', e => {
            let navPanelData:IAdComponentData = {
              left:1,
              top:1,
              visibility:'hidden'
            }
            this.knDataService.subToolBarOpen.next(navPanelData)
            this.knDataService.subUserListOpen.next({top:1,left:1,visibility:'hidden'})
            if (e.evt.ctrlKey === true) {
                let direction = e.evt.deltaY > 0 ? 1 : -1;
                this.zoomLayer(layer, e, bottomLayer, layerId);
                layerId && layerIdAbove ? this.knConnectorsService.zoomContractConnector(layerId, direction) : null;
            }
        })
        layer.on('dragstart', e => this.dragSLayertart(e));
        layer.on('dragmove', e =>
            this.knDataService.linkEdge || this.knDataService.contractConnectors.length ?
                this.draggingLayer(e, bottomLayer, layerId, layerIdAbove) : null);
        layer.on('touchmove', e => this.tabletZoomLayer(e, layer))
        layer.on('touchend', () => {
            this.knDataService.lastDist = 0;
            this.knDataService.lastCenter = null;
        })
    }

    adjustBPLayer(rect?: Rect) {
        let bpLayer = this.knDataService.layersMap.get(BUSINESS_PROCESS_LAYER_ID)
        if (!bpLayer) {
            return
        }
        let bootomContractLayer = [...this.knDataService.layersMap].filter(el => el[0] !== 'bpLayer').at(-1)?.[1]
        if (this.knDataService.linkEdge) {
            let rectModel = RectModelsMap.get('BPStatus')
            let layerFrom = this.knDataService.layersMap.get(this.knDataService.linkEdge.attrs.fromLayer);
            bpLayer.layer.absolutePosition({
                x: bpLayer.layer.getAbsolutePosition().x,
                y: (bootomContractLayer?.dragRect.getAbsolutePosition().y || 0) + (bootomContractLayer?.dragRect.height() || 0) + rectModel!.height! / 2

            })
            let fromNodeRect = (bpLayer?.layer.findOne('#' + (this.knDataService.linkEdge?.attrs?.to)) as Group)?.children.filter(child => child instanceof Rect)[0];
            let p = this.knDataService.linkEdge?.attrs?.points;
            p[p.length - 1] = fromNodeRect.getAbsolutePosition().y - (layerFrom?.layer.getAbsolutePosition().y || 0);
            this.knDataService.linkEdge.points(p);
        }
    }

    adjustLayerDimensions(nodeGroup: Group, graphLayerData: ILayerData) {
        let graphLayer = graphLayerData.layer
        let widthOutOfLayer = nodeGroup.x() + nodeGroup.attrs.dimensions.width - graphLayerData.dragRect.width()
        if (widthOutOfLayer > 0) {
            graphLayer.width(graphLayerData.dragRect.width() + widthOutOfLayer)
            graphLayerData.dragRect.width(graphLayerData.dragRect.width() + widthOutOfLayer)
        }
        let heightOutOfLayer = nodeGroup.y() + nodeGroup.attrs.dimensions.height - graphLayerData.dragRect.height()
        if (heightOutOfLayer > 0) {
            graphLayer.height(graphLayerData.dragRect.height() + heightOutOfLayer)
            graphLayerData.dragRect.height(graphLayerData.dragRect.height() + heightOutOfLayer)
        }
    }

    zoomLayer(layer: Layer, event: KonvaEventObject<WheelEvent, Layer>, bottomLayer: boolean = true, layerId: string | undefined) {
        let navPanelData:IAdComponentData = {
          left:1,
          top:1,
          visibility:'hidden'
        }
        this.knDataService.subToolBarOpen.next(navPanelData)
        this.knDataService.subUserListOpen.next({top:1,left:1,visibility:'hidden'})
        this.knDataService.subAttachementsListOpen.next({top:1,left:1,visibility:'hidden'})
        let linkEdge = this.knDataService.linkEdge as Arrow
        let p = this.knDataService.linkEdge?.attrs.points || []
        event.evt.preventDefault();
        let oldScale = layer.scaleX();
        let direction = event.evt.deltaY > 0 ? 1 : -1;
        (event.evt.ctrlKey) ? direction = -direction : null;
        let newScale = direction > 0 ? oldScale * SCALE_BY : oldScale / SCALE_BY;
        layer.scale({x: newScale, y: newScale});
        if (linkEdge && [this.knDataService.linkEdge.attrs.fromLayer, BUSINESS_PROCESS_LAYER_ID].includes(layerId)) {
            let layerFrom = this.knDataService.layersMap.get(this.knDataService.linkEdge.attrs.fromLayer);
            if (bottomLayer) {
                for (let index = 2; index < p.length - 2; index++) {
                    direction === 1 ? p[index] *= SCALE_BY : p[index] *= 1 / SCALE_BY
                }
                let toLinkEdgeNode = layer.findOne('#' + (linkEdge.attrs.to || '')) as Group
                p[p.length - 2] = toLinkEdgeNode.getAbsolutePosition().x + this.knDataService.nodeWidth / 2
                p[p.length - 1] = toLinkEdgeNode.getAbsolutePosition().y - (layerFrom?.layer.getAbsolutePosition().y || 0)
            } else {
                for (let index = 2; index < p.length; index++) {
                    direction === 1 ? p[index] /= 1.01 : p[index] /= 1 / 1.01
                }
            }
            p.length ? linkEdge.points(p) : null;
        }

    }

    tabletZoomLayer(e: KonvaEventObject<TouchEvent, Layer>, layer: Layer, bottomLayer: boolean = true) {
        e.evt.preventDefault()
        let touch1 = e.evt.targetTouches[0];
        let touch2 = e.evt.targetTouches[1];
        if (touch1 && !touch2 && !layer.isDragging() && this.knDataService.dragStopped) {
            layer.startDrag()
            this.knDataService.dragStopped = false;
        }
        if (touch1 && touch2) {
            this.knDataService.dragStopped = true;
            layer.stopDrag();
        }
        let p1 = {x: touch1.clientX, y: touch1.clientY};
        let p2 = {x: touch2.clientX, y: touch2.clientY};

        if (!this.knDataService.lastCenter) {
            this.knDataService.lastCenter = this.getCenter(p1, p2);
            return;
        }
        let newCenter = this.getCenter(p1, p2);
        let dist = Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
        !this.knDataService.lastDist ? this.knDataService.lastDist = dist : null;
        let pointTo = {
            x: (newCenter.x - layer.x()) / layer.scaleX(),
            y: (newCenter.y - layer.y()) / layer.scaleX(),
        };
        let scale = layer.scaleX() * (dist / this.knDataService.lastDist);
        let scaleDiffernce = scale - layer.scaleX()
        layer.scaleX(scale);
        layer.scaleY(scale);
        let dx = newCenter.x - this.knDataService.lastCenter.x;
        let dy = newCenter.y - this.knDataService.lastCenter.y;
        let newPos = {
            x: newCenter.x - pointTo.x * scale + dx,
            y: newCenter.y - pointTo.y * scale + dy,
        };
        layer.position(newPos);
        this.knDataService.lastDist = dist;
        this.knDataService.lastCenter = newCenter;
        let p = this.knDataService.linkEdge?.attrs.points || []
        if (bottomLayer) {
            p[p.length - 1] = p[p.length - 1] - 90 / scale * scaleDiffernce
        } else {
        }
        p.length ? this.knDataService.linkEdge.points(p) : null
    }

    dragSLayertart(e: KonvaEventObject<MouseEvent | TouchEvent, Layer>) {
        if (e.evt instanceof MouseEvent) {
            this.knDataService.dragStartPosition = {
                x: (e as KonvaEventObject<MouseEvent, Layer>).evt.clientX,
                y: (e as KonvaEventObject<MouseEvent, Layer>).evt.clientY
            }
        } else {
            this.knDataService.dragStartPosition = {
                x: (e as KonvaEventObject<TouchEvent, Layer>).evt.targetTouches[0].pageX,
                y: (e as KonvaEventObject<TouchEvent, Layer>).evt.targetTouches[0].pageY
            }
        }
    }

    draggingLayer(e: KonvaEventObject<MouseEvent | TouchEvent, Layer>, bottomLayer: boolean = true, layerId: string | undefined, layerIdAbove: string | undefined) {
        if (this.knDataService.dragStopped) {
            return
        }
        let currentPostion: Vector2d = {x: 0, y: 0}
        if (e.evt instanceof MouseEvent) {
            currentPostion = {
                x: (e as KonvaEventObject<MouseEvent, Layer>).evt.clientX,
                y: (e as KonvaEventObject<MouseEvent, Layer>).evt.clientY
            }
        } else {
            currentPostion = {
                x: (e as KonvaEventObject<TouchEvent, Layer>).evt.targetTouches[0].pageX,
                y: (e as KonvaEventObject<TouchEvent, Layer>).evt.targetTouches[0].pageY
            }
        }
        let deltaX = currentPostion.x - this.knDataService.dragStartPosition.x
        let deltaY = currentPostion.y - this.knDataService.dragStartPosition.y
        if (this.knDataService.linkEdge && ([this.knDataService.linkEdge.attrs.fromLayer, BUSINESS_PROCESS_LAYER_ID].includes(layerId))) {
            let p = this.knDataService.linkEdge?.attrs.points || [];
            if (bottomLayer) {
                for (let index = 2; index < p.length; index += 2) {
                    p[index] = p[index] + deltaX;
                    p[index + 1] = Math.max(p[index + 1] + deltaY, p[1]);
                }
            } else {
                p[p.length - 2] = p[p.length - 2] - deltaX;
                p[p.length - 1] = Math.max(p[p.length - 1] - deltaY, p[1]);
            }
            this.knDataService.linkEdge.points(p)
        }
        this.knDataService.linkEdge && (layerId === this.knDataService.linkEdge.attrs.fromLayer) ? null : null;
        if (e.evt instanceof MouseEvent) {
            this.knDataService.dragStartPosition = {
                x: (e as KonvaEventObject<MouseEvent, Layer>).evt.clientX,
                y: (e as KonvaEventObject<MouseEvent, Layer>).evt.clientY
            }
        } else {
            this.knDataService.dragStartPosition = {
                x: (e as KonvaEventObject<TouchEvent, Layer>).evt.targetTouches[0].pageX,
                y: (e as KonvaEventObject<TouchEvent, Layer>).evt.targetTouches[0].pageY
            }
        }
        if (layerId !== BUSINESS_PROCESS_LAYER_ID) {
            this.knConnectorsService.adjustContractConnector('null', {x: -deltaX, y: -deltaY}, layerId)
            this.knConnectorsService.adjustContractConnector('null', {x: deltaX, y: deltaY}, layerIdAbove)
        }
    }

    getCenter(p1: Vector2d, p2: Vector2d): Vector2d {
        return {
            x: (p1.x + p2.x) / 2,
            y: (p1.y + p2.y) / 2,
        };
    }
}